import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { ItemMasterDto } from './item-master.proxy.service';
import { PagedResult } from './bank-account.proxy.service';

/** One item on a Purchase Order (Order.API ItemInPurchaseOrderDto). Only the fields the AP document
 * consumes are typed; extra fields on the wire are ignored. */
export interface ItemInPurchaseOrderDto {
  id?: string | null;
  itemCode?: string | null;
  item?: ItemMasterDto | null;
  /** UnitOfMeasure enum code. */
  uom?: number | null;
  quantity?: number | null;
  unitPrice?: number | null;
  taxRate?: number | null;
  totalPrice?: number | null;
}

/** Trimmed Order.API PurchaseOrderDto — only what the AP document needs (key, provider, currency, items). */
export interface PurchaseOrderDto {
  id?: string | null;
  purchaseOrderNumber?: string | null;
  providerReference?: string | null;
  provider?: { id?: string; name?: string; code?: string } | null;
  /** Currency enum code (USD = 0, HNL = 1, EUR = 2). */
  currency?: number | null;
  /** PurchaseOrderStatus enum code (Order.API) — Cancelled = 7. Used to hide cancelled POs from AP pickers. */
  status?: number | null;
  /** PurchaseOrderType enum code (Order.Models) — Standard = 0. Used to restrict the Invoice picker. */
  type?: number | null;
  issuedDate?: string | null;
  /** The PO's own payment term (#4784) — beats the provider default for the AP due date. */
  paymentTermCode?: string | null;
  items?: ItemInPurchaseOrderDto[];
}

/** PurchaseOrderStatus (Order.Models) — only the value AP needs to filter on. */
export const PURCHASE_ORDER_STATUS_CANCELLED = 7;

/** PurchaseOrderType (Order.Models) — the Invoice AP picker draws Standard POs. */
export const PURCHASE_ORDER_TYPE_STANDARD = 0;

/** PurchaseOrderType (Order.Models) — confidential orders (#5942); Order.API only returns them to role holders. */
export const PURCHASE_ORDER_TYPE_ADMINISTRATIVE = 3;

export interface PurchaseOrderSearchCommand {
  purchaseOrderNumber?: string | null;
  providerReference?: string | null;
  /** PurchaseOrderType filter (Order.API PurchaseOrderSearch.Type) — null = any. */
  type?: number | null;
  /** Any of these types (Order.API PurchaseOrderSearch.Types, #5946) — OR-ed with `type`; null = any. */
  types?: number[] | null;
}

@Injectable({ providedIn: 'root' })
export class PurchaseOrderProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.orderApi + '/api/v1/purchase-order';

  public getById(id: string): Observable<ApiResponse<PurchaseOrderDto>> {
    return this.httpClient.get<ApiResponse<PurchaseOrderDto>>(`${this.baseUrl}/${id}`);
  }

  /** Batch-fetch POs by id in a single request (avoids N+1 — e.g. all POs of a pre-invoice at once). */
  public getByIds(ids: string[]): Observable<ApiResponse<PurchaseOrderDto[]>> {
    return this.httpClient.post<ApiResponse<PurchaseOrderDto[]>>(`${this.baseUrl}/batch/ids`, ids);
  }

  public search(
    command: PurchaseOrderSearchCommand,
    pageNumber = 1,
    pageSize = 10,
  ): Observable<ApiResponse<PagedResult<PurchaseOrderDto>>> {
    const params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);
    return this.httpClient.post<ApiResponse<PagedResult<PurchaseOrderDto>>>(`${this.baseUrl}/search`, command, { params });
  }
}
